import { ReactElement, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  createCategory,
  deleteCategory,
  newlyCreatedCategoryIDCleared,
  selectCategories,
  selectCategoriesStatus,
  selectCreateCategoryStatus,
  selectDeleteCategoryStatus,
  selectNewlyCreatedCategoryID,
  updateCategory,
} from '../../codex.slice'
import type { Category } from '../../types'

type Props = {
  open: boolean
  onClose: () => void
}

let CategoryManagerDialog = ({ open, onClose }: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let categories = useAppSelector(selectCategories)
  let categoriesStatus = useAppSelector(selectCategoriesStatus)
  let createCategoryStatus = useAppSelector(selectCreateCategoryStatus)
  let newlyCreatedCategoryID = useAppSelector(selectNewlyCreatedCategoryID)
  let deleteCategoryStatus = useAppSelector(selectDeleteCategoryStatus)

  let [newName, setNewName] = useState('')
  let [editingID, setEditingID] = useState<string | null>(null)
  let [editingName, setEditingName] = useState('')
  let [deletingID, setDeletingID] = useState<string | null>(null)

  let isCreating = createCategoryStatus === 'pending'

  useEffect(() => {
    if (newlyCreatedCategoryID) {
      setNewName('')
      dispatch(newlyCreatedCategoryIDCleared())
    }
  }, [newlyCreatedCategoryID, dispatch])

  useEffect(() => {
    if (deleteCategoryStatus !== 'pending') {
      setDeletingID(null)
    }
  }, [deleteCategoryStatus])

  let handleCreate = () => {
    if (!newName.trim()) return
    dispatch(createCategory({ name: newName.trim() }))
  }

  let handleStartEdit = (category: Category) => {
    setEditingID(category.id)
    setEditingName(category.name)
  }

  let handleCancelEdit = () => {
    setEditingID(null)
    setEditingName('')
  }

  let handleSaveEdit = () => {
    if (!editingID || !editingName.trim()) return
    dispatch(
      updateCategory({ id: editingID, data: { name: editingName.trim() } }),
    )
    handleCancelEdit()
  }

  let handleDelete = (id: string) => {
    setDeletingID(id)
    dispatch(deleteCategory(id))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Manage Categories</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="New category name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
            }}
          />
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newName.trim() || isCreating}
          >
            {isCreating ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </Box>

        <Divider sx={{ mb: 1 }} />

        {categoriesStatus === 'pending' && categories.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {categoriesStatus !== 'pending' && categories.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ py: 2, textAlign: 'center' }}
          >
            No categories yet.
          </Typography>
        )}

        <List disablePadding>
          {categories.map((category) => (
            <ListItem
              key={category.id}
              disablePadding
              sx={{ display: 'flex', gap: 1, py: 0.5 }}
            >
              {editingID === category.id ? (
                <>
                  <TextField
                    size="small"
                    fullWidth
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit()
                      if (e.key === 'Escape') handleCancelEdit()
                    }}
                    autoFocus
                  />
                  <IconButton
                    size="small"
                    onClick={handleSaveEdit}
                    disabled={!editingName.trim()}
                  >
                    <CheckIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={handleCancelEdit}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{ flex: 1, py: 0.75, minWidth: 0 }}
                    noWrap
                  >
                    {category.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleStartEdit(category)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(category.id)}
                    disabled={deletingID === category.id}
                  >
                    {deletingID === category.id ? (
                      <CircularProgress size={16} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </>
              )}
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default CategoryManagerDialog
